import { useState, memo } from 'react';
import {
	formatLeaderboardPoints,
	formatLeaderboardPosition,
	isTopThree,
} from 'src/_shared/services/leaderboard.js';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const LeaderboardCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		referralTotalLeaderboard,
		referralPointsLeaderboard,
	}) => {
		const cardData = card.data || {};
		const title = cardData.title || 'Leaderboards';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#595959';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 2;

		// A leaderboard the user doesn't qualify for comes back null, so it takes
		// both the admin's toggle and available data to show a tab.
		const showReferrals =
			cardData.showReferralsRanking !== false && Boolean(referralTotalLeaderboard);
		const showPoints =
			cardData.showPointsRanking !== false && Boolean(referralPointsLeaderboard);

		const [activeTab, setActiveTab] = useState('referrals');

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		// Either tab can vanish whenever the query refetches, so clamp to what's
		// actually available instead of trusting activeTab. Deriving rather than
		// syncing state also covers the first render, when both leaderboards are
		// still null and no tab is available yet.
		const availableTabs = [
			showReferrals && 'referrals',
			showPoints && 'points',
		].filter(Boolean);

		if (availableTabs.length === 0) {
			return null;
		}

		const selectedTab = availableTabs.includes(activeTab)
			? activeTab
			: availableTabs[0];
		const currentData =
			(selectedTab === 'referrals'
				? referralTotalLeaderboard
				: referralPointsLeaderboard) ?? [];
		const currentLabel = selectedTab === 'referrals' ? 'Referrals' : 'Points';

		const showTabs = availableTabs.length > 1;

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div className="d-card-head">
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
						}}
					>
						{title}
					</h4>
				</div>

				{showTabs ? (
					<div className="flex gap-2 mb-3">
						<button
							onClick={() => setActiveTab('referrals')}
							className="flex-1 py-2 px-3 text-xs font-semibold rounded transition-all"
							style={{
								backgroundColor:
									selectedTab === 'referrals' ? textColor : 'transparent',
								color: selectedTab === 'referrals' ? backgroundColor : textColor,
								fontFamily: 'Nunito Sans, sans-serif',
								border: `1px solid ${textColor}`,
								outline: 'none',
							}}
						>
							Referrals
						</button>
						<button
							onClick={() => setActiveTab('points')}
							className="flex-1 py-2 px-3 text-xs font-semibold rounded transition-all"
							style={{
								backgroundColor:
									selectedTab === 'points' ? textColor : 'transparent',
								color: selectedTab === 'points' ? backgroundColor : textColor,
								fontFamily: 'Nunito Sans, sans-serif',
								border: `1px solid ${textColor}`,
								outline: 'none',
							}}
						>
							Points
						</button>
					</div>
				) : (
					<div className="mb-3">{currentLabel}</div>
				)}

				<div className="flex-1 overflow-y-auto">
					<div className="space-y-2">
						{currentData.map((entry) => (
							<div
								key={entry.position}
								className="flex items-center justify-between py-1 px-3 rounded"
								style={{
									backgroundColor: isTopThree(entry)
										? `${textColor}15`
										: 'transparent',
									fontFamily: 'Nunito Sans, sans-serif',
								}}
							>
								<div className="flex items-center gap-3 flex-1 min-w-0">
									<span
										className="text-sm font-bold flex-shrink-0"
										style={{
											color: entry.isUser ? '#018dd3' : textColor,
											// Floor, not a fixed width: the user's own row can be
											// outside the top 100 and run to 4+ digits.
											minWidth: '24px',
										}}
									>
										{formatLeaderboardPosition(entry)}.
									</span>
									<span
										className="text-sm font-medium truncate"
										style={{ color: entry.isUser ? '#018dd3' : textColor }}
									>
										{entry.fullName}
									</span>
								</div>
								<span
									className="text-sm font-bold flex-shrink-0 ml-2"
									style={{ color: entry.isUser ? '#018dd3' : textColor }}
								>
									{formatLeaderboardPoints(entry)}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}
);

LeaderboardCard.displayName = 'LeaderboardCard';

export default LeaderboardCard;
